import React, { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, Activity, Menu, LogOut, Sun, Moon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore } from '@/stores/themeStore';
import { getMarketProfile, onMarketProfile, MarketProfilePublic } from '@/utils/format';

import { Button } from '@/components/ui/button';
import { TickFlash } from '@/components/ui/tick-flash';
interface TickerData {
  price: number;
  change: number;
  change_pct: number;
  /** Spot provenance under US profiles ('index' = the real index feed) */
  source?: string;
}

interface HeaderProps {
  onToggleSidebar: () => void;
  sidebarOpen: boolean;
  niftySpot?: TickerData | null;
  indiaVix?: TickerData | null;
  niftyFuture?: (TickerData & { expiry?: string }) | null;
  bankNifty?: TickerData | null;
  sensex?: TickerData | null;
}

// ── One ticker chip ──────────────────────────────────────────────────────────
const TickerChip: React.FC<{
  label: string;
  data?: TickerData | null;
  size?: 'lg' | 'md';
  /** small amber tag next to the price (e.g. spot 'parity' / 'proxy' flag) */
  badge?: string | null;
  /** hover text for the badge */
  badgeTitle?: string;
  // VIX bullish/bearish colour matches the change sign (up = green); for index
  // tickers the same convention applies.
}> = ({ label, data, size = 'md', badge, badgeTitle = 'Derived value — no direct index feed yet' }) => {
  const direction = data
    ? (data.change || 0) > 0 ? 'bullish'
    : (data.change || 0) < 0 ? 'bearish'
    : 'neutral'
    : 'neutral';
  const colour =
    direction === 'bullish' ? 'text-success-400' :
    direction === 'bearish' ? 'text-danger-400' :
    'text-muted-foreground';
  const Icon = direction === 'bullish' ? TrendingUp : direction === 'bearish' ? TrendingDown : Activity;
  const priceSize = size === 'lg' ? 'text-title' : 'text-body';
  const changeSize = size === 'lg' ? 'text-meta' : 'text-eyebrow';
  const iconSize = size === 'lg' ? 15 : 13;

  return (
    <div className="flex items-center gap-2.5 px-5 border-r border-border">
      <span className="text-micro font-semibold uppercase tracking-widest text-muted-foreground whitespace-nowrap">
        {label}
      </span>
      {data ? (
        <>
          <span className={`${priceSize} font-mono font-semibold text-foreground whitespace-nowrap`}>
            <TickFlash value={data.price}>{data.price.toFixed(2)}</TickFlash>
          </span>
          {badge && (
            <span
              className="text-micro font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-warning-500/15 text-warning-400 border border-warning-500/30 whitespace-nowrap"
              title={badgeTitle}
            >
              {badge}
            </span>
          )}
          <span className={`flex items-center gap-1 ${colour} ${changeSize} font-medium whitespace-nowrap`}>
            <Icon size={iconSize} />
            {Math.abs(data.change).toFixed(2)} ({Math.abs(data.change_pct).toFixed(2)}%)
          </span>
        </>
      ) : (
        <span className="text-muted-foreground text-meta">—</span>
      )}
    </div>
  );
};

export const Header: React.FC<HeaderProps> = ({
  onToggleSidebar,
  niftySpot,
  indiaVix,
  niftyFuture,
  bankNifty,
  sensex,
}) => {
  const { user, brokerActive, brokerSession, logout } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();

  // Re-render when the market profile loads (chip labels are profile-driven).
  const [profile, setProfile] = useState<MarketProfilePublic>(getMarketProfile());
  useEffect(() => onMarketProfile(setProfile), []);

  const isKite = profile.provider === 'kite';
  const spotLabel = isKite ? 'NIFTY 50' : 'S&P 500';
  const vixLabel = isKite ? 'INDIA VIX' : 'VIX';
  // Futures chip: NIFTY current month under Kite, the S&P 500 E-mini (ES)
  // front month under US profiles. The expiry month rides on the label.
  const futBase = isKite ? 'NIFTY FUT' : 'S&P 500 FUT';
  const futLabel = niftyFuture?.expiry ? `${futBase} · ${niftyFuture.expiry}` : futBase;
  // Flag any non-direct spot source ('index' is tastytrade's real feed).
  const spotBadge = !isKite && niftySpot?.source && niftySpot.source !== 'index'
    ? niftySpot.source
    : null;

  // Static chip row, ordered future → index → VIX (owner 2026-09-14): the
  // front-month future leads because it trades nearly around the clock, while
  // the index only moves in regular hours. Under US profiles the India-only
  // chips (BANK NIFTY, SENSEX) are dropped, leaving future + spot + VIX.
  const tickerSet = (
    <>
      <TickerChip label={futLabel} size="lg" data={niftyFuture ? {
        price: niftyFuture.price,
        change: niftyFuture.change,
        change_pct: niftyFuture.change_pct,
      } : null} />
      <TickerChip label={spotLabel} data={niftySpot} badge={spotBadge} />
      {isKite && <TickerChip label="BANK NIFTY" data={bankNifty} />}
      {isKite && <TickerChip label="SENSEX" data={sensex} />}
      <TickerChip label={vixLabel} data={indiaVix} />
    </>
  );

  return (
    <header className="bg-background border-b border-border h-16 sticky top-0 z-40">
      <div className="flex items-center h-full">
        {/* Hamburger */}
        <Button variant="secondary" size="inline"
          onClick={onToggleSidebar}
          className="p-2 ml-4 rounded-lg hover:bg-accent transition-colors text-muted-foreground hover:text-foreground shrink-0"
          aria-label="Toggle sidebar"
        >
          <Menu size={24} />
        </Button>

        {/* Static ticker row — takes the middle flex space, overflow-hidden
            so a narrow viewport clips rather than scrolls. */}
        <div className="flex-1 overflow-hidden mx-2">
          <div className="inline-flex items-center">
            {tickerSet}
          </div>
        </div>

        {/* Right-side: broker status + user profile */}
        <div className="flex items-center space-x-6 pr-4 shrink-0">
          <Link
            to="/settings"
            className={`flex items-center p-1.5 rounded-full border transition-all duration-300 ${
              brokerActive
                ? 'bg-success-500/10 border-success-500/20 text-success-400 hover:bg-success-500/20 shadow-[0_0_15px_-3px_rgb(34_197_94_/_0.2)]'
                : 'bg-danger-500/10 border-danger-500/20 text-danger-400 hover:bg-danger-500/20 shadow-[0_0_15px_-3px_rgb(239_68_68_/_0.2)]'
            }`}
            title={brokerActive ? `Active (${brokerSession?.user_name})` : 'Offline — click to connect broker'}
          >
            <div className={`w-2.5 h-2.5 rounded-full ${brokerActive ? 'bg-success-400 animate-pulse' : 'bg-danger-400'}`} />
          </Link>

          {/* Day / night — whole-app theme, remembered per browser */}
          <Button variant="secondary" size="inline"
            onClick={toggleTheme}
            className="p-2 rounded-xl bg-card hover:bg-accent text-muted-foreground hover:text-foreground transition-all"
            title={theme === 'dark' ? 'Switch to day mode' : 'Switch to night mode'}
            aria-label={theme === 'dark' ? 'Switch to day mode' : 'Switch to night mode'}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </Button>

          <div className="h-6 w-px bg-border hidden sm:block" />

          <div className="flex items-center gap-3">
            <div className="text-right hidden md:block">
              <div className="text-meta font-semibold text-foreground leading-tight">{user?.full_name}</div>
              <div className="text-micro font-medium text-muted-foreground leading-tight capitalize">{user?.role}</div>
            </div>
            <Button variant="secondary" size="inline"
              onClick={logout}
              className="p-2 rounded-xl bg-card hover:bg-accent text-muted-foreground hover:text-foreground transition-all group"
              title="Sign Out"
            >
              <LogOut size={18} className="group-hover:translate-x-0.5 transition-transform" />
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};
